import {AbstractAnimation} from './AbstractAnimation';

type ParsedSteps=Record<number,string>;

type ParsedAnimation={
    property:string,
    easing:string,
    duration:number,
    delay:string,
    steps:ParsedSteps
};

export class BaseAnimation extends AbstractAnimation {

    protected element!:JQuery<HTMLElement|SVGElement>;
    protected animatedElement!:JQuery<HTMLElement|SVGElement>;
    protected initialDisplay:string='';
    public fromItem!:JQuery<HTMLElement>;
    public toItem!:JQuery<HTMLElement>;
    static keyframeRules:{[key:string]:CSSRuleList}|null=null;
    private reversePlayback:boolean=false;
    private durationValue:number|null=null;

    constructor(e:JQuery<HTMLElement|SVGElement>|string) {
        super();
        if (typeof e=='string') e=$(e);
        var cn=this.getClass();
        var existing=e.data(cn);
        if (existing) return existing;
        this.element=e;
        this.animatedElement=e;
        this.initialDisplay=this.element.css('display');
        if (this.initialDisplay=='none') {
            this.element.css('display','');
            if (!this.element.is(':visible')) this.element.css('display','block');
        }
        this.element.find('>.from-'+cn).remove();
        this.element.find('>.to-'+cn).remove();
        this.fromItem=$('<span class="from from-'+cn+'"></span>').css('display','none');
        this.toItem=$('<span class="to to-'+cn+'"></span>').css('display','none');
        this.element.append(this.fromItem);
        this.element.append(this.toItem);
        if (BaseAnimation.keyframeRules===null) this.updateKeyframeRules();
        this.determineEngine();
        this.on('begin',()=>this.before());
        this.on('end',()=>this.after());
    }

    public duration(duration:number) {
        this.durationValue=duration;
        return this;
    }

    public before() {
    }

    public after() {
    }

    public assign(e:JQuery|string) {
        if (typeof e=="string") this.animatedElement=$(e); else this.animatedElement=e;
        return this;
    }

    protected px(val:number|string):string {
        return (typeof val=='number')?(val+'px'):val;
    }

    public trigger(event:string,opts={}) {
        super.trigger(event,opts);
        this.element.triggerHandler('animation.'+event);
    }

    public persist():this {
        this.element.data(this.getClass(),this);
        return this;
    }

    private determineEngine() {
        this.element.addClass(this.getClass());
        var cs=window.getComputedStyle(this.element[0]);
        var engname=cs.getPropertyValue('--animation-engine').replace(/^["'\s]+|["'\s]+$/g,'');
        this.engine(engname);
        this.element.removeClass(this.getClass());
    }

    public getAnimatedElement() {
        return this.animatedElement;
    }

    public getElement() {
        return this.element;
    }

    public getFromItem() {
        return this.fromItem;
    }

    public getToItem() {
        return this.toItem;
    }

    public getClass():string {
        return '';
    }

    public addClass(aclass:string) {
        this.element.addClass(aclass);
        return this;
    }

    public removeClass(aclass:string) {
        this.element.removeClass(aclass);
        return this;
    }

    public setCSS(e:JQuery<HTMLElement|SVGElement>,prop:string,value:string) {
        e.css(prop,value);
    }

    public getCSS(e:JQuery<HTMLElement|SVGElement>,prop:string) {
        if (prop=='border-radius') {
            var bl=e.css('borderBottomLeftRadius');
            var br=e.css('borderBottomRightRadius');
            var tl=e.css('borderTopLeftRadius');
            var tr=e.css('borderTopRightRadius');
            if (bl===br && br===tl && tl===tr) return bl;
            if (tl===br && tr===bl) return tl+' '+tr;
            if (tr===bl) return tl+' '+tr+' '+br;
            return tl+' '+tr+' '+br+' '+bl;
        }
        if (prop=='width') {
            // JQuery doesn't return percentage values properly
            return e.prop('style')['width'];
        }
        return e.css(prop);
    }

    protected isValidProperty(prop:string) {
        return true;
    }

    protected parseCSSTransitions():ParsedAnimation[] {
        var properties=this.getCSS(this.element,'transition-property').split(',');
        var easings=this.getCSS(this.element,'transition-timing-function').split(',');
        var durations=this.getCSS(this.element,'transition-duration').split(',');
        var delays=this.getCSS(this.element,'transition-delay').split(',');
        var animations:ParsedAnimation[]=[];
        for (var k=0;k<properties.length;k++) {
            if (!properties[k]) continue;
            var prop=properties[k].trim();
            if (prop=='all') continue;
            if (!this.isValidProperty(prop)) continue;
            var from=this.getCSS(this.fromItem,prop);
            var to=this.getCSS(this.toItem,prop);
            var durationMs=this.durationValue??this.convertTime(durations[k].trim());
            var animation:ParsedAnimation={
                property:prop,
                easing:easings[k].trim(),
                duration:durationMs,
                delay:delays[k].trim(),
                steps:{0:from,100:to}
            };
            animations.push(animation);
        }
        return animations;
    }

    protected getPropertiesFromRules(rules:CSSRuleList):string[] {
        var props=[];
        for (var k=0;k<rules.length;k++) {
            var rule=<CSSKeyframeRule>rules[k];
            for (var j=0;j<rule.style.length;j++) {
                var prop=rule.style[j];
                if (props.indexOf(prop)==-1) props.push(prop);
            }
        }
        return props;
    }

    protected parseCSSAnimations():ParsedAnimation[] {
        var name=this.getCSS(this.element,'animation-name').trim();
        if (!name || name=='none') return [];

        if (BaseAnimation.keyframeRules===null) return [];

        var animations:ParsedAnimation[]=[];
        var names=name.split(',');

        for (let [idx, name] of names.entries()) {
            name=name.trim();

            if (!(name in BaseAnimation.keyframeRules)) continue;

            var easings=this.getCSS(this.element,'animation-timing-function').split(',');
            var durations=this.getCSS(this.element,'animation-duration').split(',');
            var delays=this.getCSS(this.element,'animation-delay').split(',');

            var easing=easings[idx].trim();
            var duration=durations[idx].trim();
            var delay=delays[idx].trim();

            var rules:CSSRuleList=BaseAnimation.keyframeRules[name];
            var props=this.getPropertiesFromRules(rules);

            for (var j=0;j<props.length;j++) {
                var prop=props[j];
                if (!this.isValidProperty(prop)) continue;

                var steps:ParsedSteps={};
                for (var k=0;k<rules.length;k++) {
                    var rule=<CSSKeyframeRule>rules[k];
                    if (rule.keyText.slice(-1)!='%') throw 'Unexpected keyframe offset "'+rule.keyText+"'";
                    var ofs=parseFloat(rule.keyText);
                    var value=rule.style.getPropertyValue(prop);
                    if (value) steps[ofs]=value;
                }
                var durationMs=this.durationValue??this.convertTime(duration.trim());
                var animation:ParsedAnimation={
                    property:prop,
                    easing,
                    duration:durationMs,
                    delay,
                    steps
                };
                animations.push(animation);
            }
        }
        return animations;
    }

    public reverse(direction:boolean|null) {
        if (direction===null) this.reversePlayback=!this.reversePlayback; else this.reversePlayback=direction;
        return this;
    }

    public getAnimations():ParsedAnimation[] {
        this.element.addClass(this.getClass());
        this.element.css('animation','');
        this.fromItem.css('display','');
        this.toItem.css('display','');
        this.fromItem.css('position','absolute');
        this.toItem.css('position','absolute');
        var animations:ParsedAnimation[]=[];
        this.trigger('parse');
        animations=animations.concat(this.parseCSSTransitions());
        animations=animations.concat(this.parseCSSAnimations());
        this.fromItem.css('display','none');
        this.toItem.css('display','none');
        this.element.css('animation','none');
        this.fromItem.css('position','');
        this.toItem.css('position','');
        this.element.removeClass(this.getClass());

        if (this.reversePlayback) {
            for (var k=0;k<animations.length;k++) {
                var anim=animations[k];
                var steps=animations[k].steps;
                var newsteps:ParsedSteps={};
                for (var [offset,val] of Object.entries(steps)) {
                    newsteps[100-parseFloat(offset)]=val;
                }
                animations[k].steps=newsteps;
            }
        }

        return animations;
    }

    private parseSteps(anim:ParsedAnimation) {
        var steps=[];
        var apro=this.camelify(anim.property);
        for (var [offset,val] of Object.entries(anim.steps)) {
            steps.push({value:val,offset:parseFloat(offset)/100});
        }
        steps.sort((a,b)=>a.offset-b.offset);
        return steps;
    }

    public applyFrom() {
        var animations=this.getAnimations();
        this.element.css('transition','none');
        for (var anim of animations) {
            var steps=this.parseSteps(anim);
            this.element.css(anim.property,steps[0].value);
        }

        return this;
    }

    public applyTo() {
        var animations=this.getAnimations();
        this.element.css('transition','none');
        for (var anim of animations) {
            var steps=this.parseSteps(anim);
            this.element.css(anim.property,steps[steps.length-1].value);
        }

        return this;
    }

    private updateStylesheetKeyframeRules(sheet:CSSStyleSheet|CSSMediaRule) {
        var ruleList;
        try {ruleList=sheet.cssRules;} catch(e) {return;}
        if (BaseAnimation.keyframeRules===null) BaseAnimation.keyframeRules={};

        if (!ruleList) return;

        for (var rule=0;rule<ruleList.length;rule++) {
            var arule=ruleList[rule];
            if (arule instanceof CSSImportRule) {
                if (arule.styleSheet) this.updateStylesheetKeyframeRules(arule.styleSheet);
                continue;
            }
            if (arule instanceof CSSMediaRule) {
                this.updateStylesheetKeyframeRules(arule);
                continue;
            }
            if (!(arule instanceof CSSKeyframesRule)) continue;

            BaseAnimation.keyframeRules[arule.name]=arule.cssRules;
        }
    }

    private updateKeyframeRules() {
        for (let sheet of document.styleSheets) this.updateStylesheetKeyframeRules(sheet);
        }

}

export {};